/**
 * SMS WebSocket connection provider.
 *
 * Mirrors AIM's SocketContext / useSocketService (ADR-049):
 *   - single WS connection for the whole app
 *   - exponential backoff with jitter on unexpected close
 *   - fast-path reconnect for graceful close codes (4001, 4010)
 *   - stop-and-clear session on auth-failure close code (4002)
 *   - `supports_ack: true` capabilities handshake on open
 *   - ACK reply on messages flagged with `requires_ack`
 *   - every connect attempt exchanges the JWT for a single-use ticket and
 *     connection descriptor (`POST /api/v2/ws/ticket/`, ADR-078 Amendment
 *     1, 6-F) and opens `<descriptor url>?ticket=<ticket>`, trying the
 *     allowlisted `fallback[]` entries in order. The JWT never enters a
 *     socket URL; an attempt without a ticket falls into backoff.
 *
 * School-wide reunification visibility: the backend broadcasts to the
 * `school-reunification-{school_id}` channel group, but the EverythingConsumer
 * in apps/aegix-aim-backend/server/api/v2/consumers.py has no `action:
 * 'subscribe'` branch (a client-sent subscribe frame is a no-op) and joins no
 * `school-reunification-*` group on connect, so SMS receives reunification
 * events only via the `user-{id}` group the consumer auto-joins. That covers
 * events the backend explicitly targets at the user; school-wide scope waits
 * on a server-side group_add, tracked as a follow-on to AAS-988.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import {
  onTokenRefreshComplete,
  socketUrlWithTicket,
  type SocketConnectionPlan,
} from '@tiaki/http-client';
import { useAuth } from '@aegix/auth-identity';
import {
  buildSocketConnectionPlan,
  configuredSocketEndpoint,
} from '../lib/connectionDescriptor';
import { client } from '../lib/http-client';
import { type SMSUser, getSmsAccessToken } from '../lib/sms-auth';

// ADR-049 reconnection knobs — identical shape to AIM's socket service.
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;
const JITTER_FACTOR = 0.2;
const GRACEFUL_RECONNECT_DELAY_MS = 500;
const HARD_MAX_RETRIES = 50;
const STABLE_CONNECTION_THRESHOLD_MS = 30_000;
const FALLBACK_ATTEMPT_DELAY_MS = 500; // The descriptor's next entry is tried without waiting out backoff

// Private-use WS close codes (RFC 6455 4000-4999).
export const WS_CLOSE_CODES = {
  PONG_TIMEOUT: 4000,
  TOKEN_REFRESH: 4001,
  AUTH_FAILED: 4002,
  SERVER_SHUTDOWN: 4010,
} as const;

const GRACEFUL_CLOSE_CODES = new Set<number>([
  WS_CLOSE_CODES.TOKEN_REFRESH,
  WS_CLOSE_CODES.SERVER_SHUTDOWN,
]);

const AUTH_FAILURE_CLOSE_CODES = new Set<number>([WS_CLOSE_CODES.AUTH_FAILED]);

export type ConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'auth_failed';

export interface ConnectionState {
  status: ConnectionStatus;
  lastConnected: Date | null;
  reconnectAttempt: number;
}

type MessageListener = (raw: string) => void;

interface SocketContextValue {
  connectionState: ConnectionState;
  isConnected: boolean;
  isReconnecting: boolean;
  addMessageListener: (listener: MessageListener) => void;
  removeMessageListener: (listener: MessageListener) => void;
}

const SocketContext = createContext<SocketContextValue | null>(null);

function addJitter(delay: number): number {
  const jitter = delay * JITTER_FACTOR * (Math.random() * 2 - 1);
  return Math.round(delay + jitter);
}

function getReconnectDelay(retryCount: number): number {
  const exp = Math.min(BASE_DELAY_MS * Math.pow(2, retryCount), MAX_DELAY_MS);
  return addJitter(exp);
}

/**
 * Mint the ticket for one connect attempt on the typed client, so the
 * exchange is authenticated, refreshed and retried like every SMS REST call.
 * The JWT is named explicitly because, on the render that authenticates, the
 * auth provider persists it in an effect that runs after this one. A 401 here
 * has already been through the client's refresh, so the session is over.
 *
 * Device identity: the socket's `device_id` is copied
 * off the verified access token's signed claim when the ticket is minted
 * (`server/api/v2/ws_ticket.py`) — the claim the LOGIN request's
 * `x-device-id` header established (see `lib/deviceIdHeader.ts`). The
 * ticket exchange deliberately does NOT restate `x-device-id`: the backend
 * ignores a socket's own claim of identity, which is what makes the
 * binding unforgeable.
 */
async function requestTicket(
  token: string,
  signal: AbortSignal,
): Promise<SocketConnectionPlan | 'auth_failed' | null> {
  try {
    const { data, response } = await client.POST('/api/v2/ws/ticket/', {
      headers: { Authorization: `JWT ${token}` },
      signal,
    });
    if (response.status === 401) return 'auth_failed';
    if (!response.ok) return null;
    return buildSocketConnectionPlan(data);
  } catch {
    return null;
  }
}

export interface SocketProviderProps extends PropsWithChildren {
  /**
   * Optional factory for testing — lets tests inject a MockWebSocket without
   * touching the global. Defaults to the native WebSocket constructor.
   */
  socketFactory?: (url: string) => WebSocket;
}

export function SocketProvider({ children, socketFactory }: SocketProviderProps) {
  // Read the raw JWT directly from the lib's auth state via
  // `useAuth().state.jwt` rather than localStorage. The persistence
  // effect (lib-side) writes to localStorage on the SAME commit that
  // sets state.jwt, but effects run child-first — by the time this
  // provider's effect runs `connect()`, the parent AuthProvider's
  // persistence effect hasn't fired yet, so `localStorage.getItem`
  // returns stale (or null) values. Reading from state is
  // synchronous with the render that flipped the user to
  // authenticated. Falls back to localStorage when state.jwt is empty
  // (cookie-only auth) or the legacy `__cookie__` sentinel, so the WS
  // still has a real token to send.
  const auth = useAuth<SMSUser>();
  const stateToken = (() => {
    switch (auth.state.status) {
      case 'authenticated':
      case 'refreshing':
        return auth.state.jwt && auth.state.jwt !== '__cookie__'
          ? auth.state.jwt
          : null;
      default:
        return null;
    }
  })();
  const isLoggedIn =
    auth.state.status === 'authenticated' ||
    auth.state.status === 'refreshing';

  const socketRef = useRef<WebSocket | null>(null);
  const listenersRef = useRef<MessageListener[]>([]);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stabilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ticketDeadlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const retryCountRef = useRef(0);
  // Whether the current connection outlived the stability window. A close
  // before it is a failed attempt (transport or ticket), which moves on to the
  // descriptor's next endpoint; a close after it is a dropped session.
  const connectionStableRef = useRef(false);
  // The current descriptor's endpoints and the cursor into them. The cursor
  // advances on a failed attempt and returns to the primary once a connection
  // proves stable or the endpoints run out.
  const endpointsRef = useRef<string[]>([]);
  const endpointIndexRef = useRef(0);
  // Bumped on teardown so a ticket exchange still in flight opens nothing.
  const connectEpochRef = useRef(0);
  const ticketRequestRef = useRef<AbortController | null>(null);

  const [connectionState, setConnectionState] = useState<ConnectionState>({
    status: 'idle',
    lastConnected: null,
    reconnectAttempt: 0,
  });

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearStabilityTimer = useCallback(() => {
    if (stabilityTimerRef.current) {
      clearTimeout(stabilityTimerRef.current);
      stabilityTimerRef.current = null;
    }
  }, []);

  const clearTicketDeadline = useCallback(() => {
    if (ticketDeadlineTimerRef.current) {
      clearTimeout(ticketDeadlineTimerRef.current);
      ticketDeadlineTimerRef.current = null;
    }
  }, []);

  // Hold the latest JWT in a ref so `connect()` always sees
  // the current token without re-creating the callback on every
  // token rotation. Two sources contribute:
  //
  //   1. `stateToken` (above) — the JWT inside the lib's auth state.
  //      This is the canonical source and updates synchronously with
  //      the render that flips `authenticated`. Token rotations via
  //      `actions.refresh()` also update it.
  //
  //   2. localStorage `aegix_sms_token` — the legacy SMS key that
  //      `@tiaki/http-client` writes when it rotates a token via the
  //      `x-refreshed-token` response header. The lib's
  //      auth state doesn't see that rotation (it happens entirely
  //      inside http-client's middleware), so the ref falls back to
  //      localStorage so a fresh ticket exchange uses the rotated
  //      token. The `onTokenRefreshComplete` listener below schedules
  //      a graceful WS reconnect (4001) when http-client rotates,
  //      which re-runs `connect()` and picks up the new value here.
  //
  // With `stateToken` in a ref, token rotations through the lib
  // don't tear down the socket: the main effect's
  // deps only change on login/logout (`isLoggedIn`), not on every
  // token mutation.
  const tokenRef = useRef<string | null>(stateToken);
  tokenRef.current = stateToken;

  const connectRef = useRef<() => void>(() => undefined);
  const socketFactoryRef = useRef(socketFactory);
  socketFactoryRef.current = socketFactory;

  const readStoredToken = useCallback((): string | null => {
    if (tokenRef.current) return tokenRef.current;
    // Read the rotated JWT through the SMS auth accessor
    // (raw token in `aegix_sms_token`) rather than localStorage directly.
    return getSmsAccessToken();
  }, []);

  // Detach handlers BEFORE closing so the cleanup-initiated close does
  // not re-enter the reconnect-backoff branch (code 1000 falls through
  // to the "unexpected close" path), and an unmount — normal in tests,
  // logout, hot-reload — leaves no pending timer that spawns a new
  // WebSocket against a stale factory.
  const detachAndClose = useCallback((socket: WebSocket | null) => {
    if (!socket) return;
    socket.onopen = null;
    socket.onclose = null;
    socket.onmessage = null;
    socket.onerror = null;
    try {
      socket.close();
    } catch {
      /* ignore */
    }
  }, []);

  /** ADR-049 exponential backoff with jitter. */
  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) return;
    if (retryCountRef.current >= HARD_MAX_RETRIES) {
      setConnectionState((prev) => ({ ...prev, status: 'disconnected' }));
      return;
    }

    const delay = getReconnectDelay(retryCountRef.current);
    setConnectionState((prev) => ({
      ...prev,
      status: 'reconnecting',
      reconnectAttempt: retryCountRef.current + 1,
    }));
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      retryCountRef.current += 1;
      connectRef.current();
    }, delay);
  }, []);

  // A failed attempt: the descriptor's next endpoint gets a turn with a
  // fresh ticket; with none left the cycle returns to the primary and falls
  // into backoff.
  const scheduleNextEndpoint = useCallback(() => {
    if (reconnectTimerRef.current) return;
    if (endpointIndexRef.current + 1 < endpointsRef.current.length) {
      endpointIndexRef.current += 1;
      setConnectionState((prev) => ({
        ...prev,
        status: 'reconnecting',
        reconnectAttempt: retryCountRef.current + 1,
      }));
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connectRef.current();
      }, FALLBACK_ATTEMPT_DELAY_MS);
      return;
    }
    endpointIndexRef.current = 0;
    scheduleReconnect();
  }, [scheduleReconnect]);

  // The ticket is single-use and lapses after the lifetime the server
  // declared, so a handshake still pending by then can no longer complete
  // on it. No deadline when the server declared none.
  const armTicketDeadline = useCallback(
    (socket: WebSocket, lifetimeMs: number | null) => {
      if (lifetimeMs === null) return;
      ticketDeadlineTimerRef.current = setTimeout(() => {
        ticketDeadlineTimerRef.current = null;
        if (socketRef.current !== socket) return;
        if (socket.readyState === WebSocket.OPEN) return;
        socketRef.current = null;
        detachAndClose(socket);
        scheduleNextEndpoint();
      }, lifetimeMs);
    },
    [detachAndClose, scheduleNextEndpoint],
  );

  /** Cancel every timer and in-flight exchange and close the socket. */
  const teardown = useCallback(() => {
    connectEpochRef.current += 1;
    ticketRequestRef.current?.abort();
    ticketRequestRef.current = null;
    clearReconnectTimer();
    clearStabilityTimer();
    clearTicketDeadline();
    detachAndClose(socketRef.current);
    socketRef.current = null;
    connectionStableRef.current = false;
    endpointIndexRef.current = 0;
  }, [
    clearReconnectTimer,
    clearStabilityTimer,
    clearTicketDeadline,
    detachAndClose,
  ]);

  const connect = useCallback(() => {
    // `connect` runs async internally so the caller can treat it like a
    // normal imperative trigger; unhandled errors in the async body
    // surface through onclose → backoff.
    void (async () => {
      const storedToken = readStoredToken();
      if (!storedToken) return;
      if (!configuredSocketEndpoint()) return;

      clearReconnectTimer();

      const epoch = connectEpochRef.current;
      const ticketRequest = new AbortController();
      ticketRequestRef.current = ticketRequest;
      const plan = await requestTicket(storedToken, ticketRequest.signal);
      if (ticketRequestRef.current === ticketRequest) {
        ticketRequestRef.current = null;
      }
      // A teardown during the exchange owns the outcome.
      if (connectEpochRef.current !== epoch) return;

      if (plan === 'auth_failed') {
        setConnectionState({
          status: 'auth_failed',
          lastConnected: null,
          reconnectAttempt: 0,
        });
        retryCountRef.current = 0;
        return;
      }
      if (!plan) {
        scheduleReconnect();
        return;
      }

      // A logout that raced with the ticket fetch may have cleared
      // localStorage by now — re-check before opening the socket so
      // we don't connect with stale credentials. The parent effect
      // observes `useIsAuthenticated()` and also tears down on
      // logout, but that fires on the next render cycle.
      if (!readStoredToken()) return;

      detachAndClose(socketRef.current);
      socketRef.current = null;

      // A fresher descriptor can be shorter than the one this cycle started on.
      endpointsRef.current = plan.endpoints;
      if (endpointIndexRef.current >= plan.endpoints.length) {
        endpointIndexRef.current = 0;
      }
      const endpoint = plan.endpoints[endpointIndexRef.current];

      const factory = socketFactoryRef.current ?? ((u: string) => new WebSocket(u));
      const socket = factory(socketUrlWithTicket(endpoint, plan.ticket));
      socketRef.current = socket;
      connectionStableRef.current = false;
      armTicketDeadline(socket, plan.ticketLifetimeMs);

      // Attach close BEFORE open so failed connection attempts also trigger reconnect.
      socket.onclose = (event: CloseEvent) => {
        clearStabilityTimer();
        clearTicketDeadline();
        if (socketRef.current === socket) socketRef.current = null;
        const wasStable = connectionStableRef.current;
        connectionStableRef.current = false;

        if (AUTH_FAILURE_CLOSE_CODES.has(event.code) && wasStable) {
          setConnectionState({
            status: 'auth_failed',
            lastConnected: null,
            reconnectAttempt: 0,
          });
          retryCountRef.current = 0;
          return;
        }

        if (GRACEFUL_CLOSE_CODES.has(event.code)) {
          retryCountRef.current = 0;
          setConnectionState((prev) => ({
            ...prev,
            status: 'connecting',
            reconnectAttempt: 0,
          }));
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            connectRef.current();
          }, GRACEFUL_RECONNECT_DELAY_MS);
          return;
        }

        // Before the stability window, an auth-failure close is the server
        // refusing this attempt's ticket, not proof the session is over: the
        // next attempt mints a fresh ticket, and a dead session surfaces there
        // as a 401 from the ticket endpoint.
        if (!wasStable) {
          scheduleNextEndpoint();
          return;
        }

        scheduleReconnect();
      };

      socket.onopen = () => {
        clearTicketDeadline();

        clearStabilityTimer();
        stabilityTimerRef.current = setTimeout(() => {
          stabilityTimerRef.current = null;
          retryCountRef.current = 0;
          connectionStableRef.current = true;
          endpointIndexRef.current = 0;
        }, STABLE_CONNECTION_THRESHOLD_MS);

        setConnectionState({
          status: 'connected',
          lastConnected: new Date(),
          reconnectAttempt: 0,
        });

        // Declare ACK support BEFORE processing messages so the
        // server replays pending critical events (reunification.activated
        // et al.) that arrived while we were offline.
        try {
          socket.send(
            JSON.stringify({
              type: 'capabilities',
              supports_ack: true,
              client: 'sms-web',
              version: '1.0.0',
            }),
          );
        } catch {
          /* socket closed mid-handshake — onclose will handle it */
        }

        // NOTE: no subscribe frame is sent here. `EverythingConsumer`
        // auto-joins `user-{user_id}` on accept, which is enough for
        // backend broadcasts that target the user directly. School-wide
        // reunification scope (`school-reunification-{school_id}`)
        // requires a backend-side group_add — tracked as a follow-on to
        // AAS-988. Sending a client `{action: 'subscribe', ...}` frame
        // is a no-op against the current consumer and would create a
        // false sense of coverage.
      };

      socket.onmessage = (ev: MessageEvent) => {
        const raw = typeof ev.data === 'string' ? ev.data : '';
        if (!raw) return;

        // Reply with ACK so the server stops replaying.
        try {
          const parsed = JSON.parse(raw);
          if (parsed?.requires_ack && parsed?.id) {
            socket.send(JSON.stringify({ type: 'ack', message_id: parsed.id }));
          }
        } catch {
          /* non-JSON frame — still deliver to listeners */
        }

        for (const listener of listenersRef.current) {
          try {
            listener(raw);
          } catch (err) {
            console.error('[SocketProvider] listener error:', err);
          }
        }
      };

      socket.onerror = () => {
        /* onclose fires right after — reconnect is handled there */
      };
    })();
  }, [
    armTicketDeadline,
    clearReconnectTimer,
    clearStabilityTimer,
    clearTicketDeadline,
    detachAndClose,
    readStoredToken,
    scheduleNextEndpoint,
    scheduleReconnect,
  ]);

  connectRef.current = connect;

  useEffect(() => {
    if (!isLoggedIn) {
      teardown();
      retryCountRef.current = 0;
      setConnectionState({ status: 'idle', lastConnected: null, reconnectAttempt: 0 });
      return;
    }

    setConnectionState((prev) => ({ ...prev, status: 'connecting' }));
    connect();

    return teardown;
  }, [isLoggedIn, connect, teardown]);

  // Subscribe to the lib's token-refresh listener so the socket
  // reconnects with the rotated JWT instead of staying open with a stale
  // one. Closing with WS_CLOSE_CODES.TOKEN_REFRESH (4001) routes through
  // GRACEFUL_CLOSE_CODES → fast-path reconnect (≤500ms) which re-runs
  // the ticket exchange against the freshly-stored token. This is the
  // first time SMS WS survives a backend-initiated token rotation without
  // dropping the user — the named harm ADR 113 opens with.
  useEffect(() => {
    const unsubscribe = onTokenRefreshComplete(() => {
      const live = socketRef.current;
      if (live && live.readyState === WebSocket.OPEN) {
        try {
          live.close(WS_CLOSE_CODES.TOKEN_REFRESH, 'token-rotated');
        } catch {
          /* close raced with another lifecycle event — onclose handles it */
        }
      }
    });
    return unsubscribe;
  }, []);

  const addMessageListener = useCallback((listener: MessageListener) => {
    listenersRef.current.push(listener);
  }, []);

  const removeMessageListener = useCallback((listener: MessageListener) => {
    const idx = listenersRef.current.indexOf(listener);
    if (idx !== -1) listenersRef.current.splice(idx, 1);
  }, []);

  const value = useMemo<SocketContextValue>(
    () => ({
      connectionState,
      isConnected: connectionState.status === 'connected',
      isReconnecting: connectionState.status === 'reconnecting',
      addMessageListener,
      removeMessageListener,
    }),
    [connectionState, addMessageListener, removeMessageListener],
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket(): SocketContextValue {
  const ctx = useContext(SocketContext);
  if (!ctx) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return ctx;
}

export function useSocketMessage(handler: MessageListener) {
  const { addMessageListener, removeMessageListener } = useSocket();
  useEffect(() => {
    addMessageListener(handler);
    return () => removeMessageListener(handler);
  }, [handler, addMessageListener, removeMessageListener]);
}

export default SocketContext;
